import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  Share2, Download, Copy, X,
  FileText, MessageCircle, Twitter, Facebook, Link2, Mail, Smartphone
} from 'lucide-react';
import { isNative, nativeShare } from '../../utils/capacitor';

export const BlogShareDialog = ({
  show,
  onClose,
  shareUrl,
  blog,
  pdfFileName,
  onDownloadPdf,
}) => {
  const shareText = blog
    ? `Read my travel blog: "${blog.title}" - Created with Triple A Travel Solutions. Plan your trip at tripleabooking.com`
    : '';

  const handleNativeShare = async () => {
    const shared = await nativeShare.share({
      title: blog?.title || 'My Travel Blog',
      text: shareText,
      url: shareUrl,
    });
    if (shared) toast.success('Shared!');
  };

  const copyShareLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied!'); }
    catch { toast.error('Failed to copy'); }
  };
  const shareWhatsApp = () => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`, '_blank');
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
  const shareEmail = () => {
    const subject = encodeURIComponent(blog?.title || 'My Travel Blog');
    const body = encodeURIComponent(`${shareText}\n\nRead my blog: ${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={e => e.stopPropagation()}
            data-testid="blog-share-dialog"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-ocean font-semibold text-lg flex items-center gap-2">
                <Share2 className="w-5 h-5 text-gold" />
                Share Your Blog
              </h3>
              <button onClick={onClose} className="text-charcoal/40 hover:text-charcoal">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-ocean/5 border border-ocean/15 rounded-xl p-4 mb-4 flex items-center gap-3" data-testid="blog-pdf-display">
              <div className="w-10 h-10 bg-ocean/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-ocean" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ocean truncate">{pdfFileName}</p>
                <p className="text-xs text-charcoal/50">PDF Travel Blog</p>
              </div>
              <Button onClick={onDownloadPdf} size="sm" className="bg-gold hover:bg-gold/90 text-ocean rounded-full text-xs px-4 h-8 font-semibold shadow" data-testid="blog-download-pdf">
                <Download className="w-3.5 h-3.5 mr-1" /> Download
              </Button>
            </div>

            <p className="text-charcoal/60 text-sm mb-2">Share your blog link:</p>
            <div className="flex gap-2 mb-4">
              <input type="text" readOnly value={shareUrl} className="flex-1 bg-gray-100 rounded-lg px-3 py-2 text-sm text-charcoal border border-gray-200" data-testid="blog-share-url" />
              <Button onClick={copyShareLink} className="bg-ocean hover:bg-ocean-light text-white rounded-lg" data-testid="blog-copy-link">
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {isNative && (
                <Button onClick={handleNativeShare} className="rounded-xl h-11 bg-ocean text-white hover:bg-ocean-light col-span-2" data-testid="blog-native-share-btn">
                  <Smartphone className="w-4 h-4 mr-2" /> Share via Device
                </Button>
              )}
              <Button onClick={shareWhatsApp} variant="outline" className="rounded-xl h-11 border-green-300 text-green-700 hover:bg-green-50" data-testid="blog-whatsapp-btn">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
              <Button onClick={shareTwitter} variant="outline" className="rounded-xl h-11 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="blog-twitter-btn">
                <Twitter className="w-4 h-4 mr-2" /> Twitter / X
              </Button>
              <Button onClick={shareFacebook} variant="outline" className="rounded-xl h-11 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="blog-facebook-btn">
                <Facebook className="w-4 h-4 mr-2" /> Facebook
              </Button>
              <Button onClick={copyShareLink} variant="outline" className="rounded-xl h-11 border-ocean/20 text-ocean hover:bg-ocean/5" data-testid="blog-copy-link-2">
                <Link2 className="w-4 h-4 mr-2" /> Copy Link
              </Button>
              <Button onClick={shareEmail} variant="outline" className="rounded-xl h-11 border-amber-300 text-amber-700 hover:bg-amber-50 col-span-2" data-testid="blog-email-btn">
                <Mail className="w-4 h-4 mr-2" /> Email to Friends
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
